import type { AccessToken, AccessTokenStatus, CreatedAccessToken } from '@pm/schemas';
import {
  Badge,
  type BadgeTone,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  Select,
  SkeletonRows,
} from '@pm/ui';
import { useQuery } from '@tanstack/react-query';
import { Bot, Copy, KeyRound, Plus } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { copyText } from '@/features/forms/form-bits';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

const KEY = ['access-tokens'] as const;

const STATUS: Record<AccessTokenStatus, { tone: BadgeTone; label: string }> = {
  ACTIVE: { tone: 'success', label: 'Ativo' },
  EXPIRED: { tone: 'warning', label: 'Expirado' },
  REVOKED: { tone: 'neutral', label: 'Revogado' },
};

/**
 * Tokens de acesso para agentes (MCP). O agente age como você, mas só lê as suas demandas,
 * tarefas pessoais e anotações, cria, atualiza e define o responsável. Nunca exclui nada.
 */
export function AccessTokensCard() {
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<AccessToken[]>('/me/access-tokens'),
  });
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<AccessToken | null>(null);
  const revoke = useInvalidating((id: string) => api.post(`/me/access-tokens/${id}/revoke`), [KEY]);

  return (
    <div className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <LabelCaps>Tokens de acesso (agentes)</LabelCaps>
          <p className="mt-1 text-sm text-text-3">
            Conecte um agente de IA ao WIP por MCP. Ele age como você, mas só lê as suas demandas,
            tarefas pessoais e anotações, cria, atualiza e define o responsável. Nunca exclui nada.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
          <Plus /> Gerar token
        </Button>
      </div>

      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : isError || !data ? (
        <ErrorState message={error?.message} onRetry={() => void refetch()} />
      ) : data.length === 0 ? (
        <EmptyState
          icon={Bot}
          title="Nenhum token"
          description="Gere um token para conectar um agente, como o Luka."
        />
      ) : (
        <ul className="flex flex-col">
          {data.map((t) => (
            <li
              key={t.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border py-2.5 last:border-b-0"
            >
              <KeyRound className="size-4 text-text-3" aria-hidden />
              <span className="font-medium">{t.name}</span>
              <span className="font-mono text-xs text-text-3">{t.prefix}…</span>
              <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge>
              <span className="ml-auto flex flex-wrap items-center gap-x-3 font-mono text-xs text-text-3">
                <span>criado {fmtDateTime(t.created_at)}</span>
                <span>uso {t.last_used_at ? fmtDateTime(t.last_used_at) : 'nunca'}</span>
                <span>{t.expires_at ? `vence ${fmtDateTime(t.expires_at)}` : 'sem validade'}</span>
              </span>
              {t.status === 'ACTIVE' ? (
                <Button variant="ghost" size="sm" onClick={() => setRevoking(t)}>
                  Revogar
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <CreateTokenDialog open={creating} onOpenChange={setCreating} />
      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={`Revogar o token "${revoking?.name ?? ''}"?`}
        description="O agente que usa este token perde o acesso na hora. Não dá para desfazer; se precisar, gere outro."
        confirmLabel="Revogar"
        danger
        loading={revoke.isPending}
        onConfirm={() =>
          revoking &&
          revoke.mutate(revoking.id, {
            onSuccess: () => (toast.success('Token revogado'), setRevoking(null)),
            onError: (err) => toast.error(err),
          })
        }
      />
    </div>
  );
}

function CreateTokenDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Gerar token de acesso" size="md">
        {open ? <CreateTokenForm onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function CreateTokenForm({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [ttl, setTtl] = useState<'30' | '90' | '365' | 'none'>('90');
  const [created, setCreated] = useState<CreatedAccessToken | null>(null);
  const create = useInvalidating(
    () =>
      api.post<CreatedAccessToken>('/me/access-tokens', {
        name: name.trim(),
        expires_in_days: ttl === 'none' ? null : Number(ttl),
      }),
    [KEY],
  );

  if (created) {
    const copy = (text: string, what: string) =>
      void copyText(text).then((ok) => ok && toast.success(`${what} copiado`));
    return (
      <div className="flex flex-col gap-4">
        <p className="rounded border border-border bg-surface-2 px-3 py-2 text-sm text-text-2">
          Copie o token agora. Por segurança ele não será mostrado de novo.
        </p>
        <Field label="Token" htmlFor="tk-value">
          <div className="flex gap-2">
            <Input id="tk-value" mono readOnly value={created.token} className="flex-1" />
            <Button variant="secondary" onClick={() => copy(created.token, 'Token')}>
              <Copy /> Copiar
            </Button>
          </div>
        </Field>
        <Field
          label="Endereço MCP"
          htmlFor="tk-url"
          hint="No agente, configure um servidor MCP HTTP com o cabeçalho Authorization: Bearer <token>."
        >
          <div className="flex gap-2">
            <Input id="tk-url" mono readOnly value={created.mcp_url} className="flex-1" />
            <Button variant="secondary" onClick={() => copy(created.mcp_url, 'Endereço')}>
              <Copy /> Copiar
            </Button>
          </div>
        </Field>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Concluir
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(undefined, {
          onSuccess: (r) => setCreated(r),
          onError: (err) => toast.error(err),
        });
      }}
    >
      <Field
        label="Nome"
        htmlFor="tk-name"
        hint="Para reconhecer o agente no histórico. Ex.: Luka."
      >
        <Input
          id="tk-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          autoFocus
        />
      </Field>
      <Field label="Validade" htmlFor="tk-ttl">
        <Select id="tk-ttl" value={ttl} onChange={(e) => setTtl(e.target.value as typeof ttl)}>
          <option value="30">30 dias</option>
          <option value="90">90 dias</option>
          <option value="365">1 ano</option>
          <option value="none">Sem validade</option>
        </Select>
      </Field>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={create.isPending}
          disabled={name.trim().length < 2}
        >
          Gerar token
        </Button>
      </DialogFooter>
    </form>
  );
}
